import {objectFromEntries} from '@benchling/js-utils';

import {assert} from 'Asserts';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PIPELINE_STEP_SHAPES} from 'pipelines/GeneratedPipelineStepShapes';
import {StepConfigShape} from 'pipelines/StepShapeUtils';
import {JSONValue} from 'util/Types';

import {STEP_CONFIG_SETTINGS, STEP_CONFIG_SETTINGS_V2} from '../step-config-settings/StepConfigSettings';
import {
  ShapeUISchema,
  ShapeUISchemaV2,
  getFieldSettingsIfPresent,
  getFieldSettingsIfPresentV2,
} from '../StepConfigEditorTypes';
import {isUiSchemaForStepTypeV2, stepTypeIsConfigEditorSupported} from '../stepTypeIsConfigEditorSupported';
import jsonSpecToInitialValue from './jsonSpecToInitialValue';

export function makeInitialConfigStateFromConfigShape(
  configShape: StepConfigShape,
  uiSchema: ShapeUISchema | ShapeUISchemaV2,
  isV2Schema: boolean
): Record<string, JSONValue> {
  return objectFromEntries(
    // TODO(BNCH-148338): Remove V1 path once all steps are migrated to V2.
    Object.entries(configShape).map(([variableName, jsonSpec]) => [
      variableName,
      jsonSpecToInitialValue(
        jsonSpec,
        isV2Schema
          ? getFieldSettingsIfPresentV2(uiSchema as ShapeUISchemaV2, variableName)
          : getFieldSettingsIfPresent(uiSchema as ShapeUISchema, variableName)
      ),
    ])
  );
}

export default function makeInitialConfigState(
  stepType: PipelineStepType,
  stepVersion: number
): Record<string, JSONValue> {
  assert(
    stepTypeIsConfigEditorSupported(stepType, stepVersion) && stepType !== PipelineStepType.CHART_VIEW,
    `Unexpected ${stepType} v${stepVersion}`
  );
  const configVariablesShape = PIPELINE_STEP_SHAPES[stepType][stepVersion].config as StepConfigShape;

  // V1 and V2 schemas live in separate settings maps with different structures.
  // TODO(BNCH-148338): Remove V1 path and consolidate into a single map once all steps are migrated to V2.
  const isV2Schema = isUiSchemaForStepTypeV2(stepType);
  let uiSchema: ShapeUISchema | ShapeUISchemaV2;
  if (isV2Schema) {
    uiSchema = STEP_CONFIG_SETTINGS_V2[stepType][stepVersion]?.uiSchema as ShapeUISchemaV2;
  } else {
    uiSchema = STEP_CONFIG_SETTINGS[stepType]?.[stepVersion]?.uiSchema as ShapeUISchema;
  }
  return makeInitialConfigStateFromConfigShape(configVariablesShape, uiSchema, isV2Schema);
}
